import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Table } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import { LEGAL_FORMALITIES_INDEX_QUERY } from 'graphql/LegalFormalitiesIndex'
import CentredLoader from 'components/CentredLoader'
import type { SorterResult } from 'antd/es/table/interface'
import { LegalFormalityIndexTableType } from './types'
import { columns } from './config'
import { VariablesContext } from '../../contexts/variablesContext'
import SearchSection from '../SearchSection'
import styled from 'styled-components'
import FormalitiesActionsMenu from '../FormalitiesActionsMenu'
import { AbilityContext } from 'contexts/abilityContext'
import { LEGALISTS_QUERY } from 'graphql/User'

const LegalFormalitiesTable = (): JSX.Element => {
  const { t } = useTranslation()
  const { variables, setVariables, apiParameters } =
    useContext(VariablesContext)
  const ability: any = useContext(AbilityContext)

  const { data, loading } = useQuery(LEGAL_FORMALITIES_INDEX_QUERY, {
    variables: { ...apiParameters() },
    fetchPolicy: 'no-cache',
  })

  const { data: legalisteData } = useQuery(LEGALISTS_QUERY, {
    fetchPolicy: 'network-only',
  })

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any> = {},
    sorter:
      | SorterResult<LegalFormalityIndexTableType>
      | SorterResult<LegalFormalityIndexTableType>[] = []
  ) => {
    const updatedVariables = {
      ...variables,
      pagination: pagination || variables.pagination,
      filters: filters || variables.filters,
      sorter: sorter || variables.sorter,
    }

    sessionStorage.setItem('filters', JSON.stringify(updatedVariables))
    setVariables(updatedVariables)
  }

  if (loading || !legalisteData) {
    return (
      <>
        <FormalitiesActionsWrapper>
          <SearchSection tableLoading={loading} />
        </FormalitiesActionsWrapper>
        <CentredLoader />
      </>
    )
  }

  const legalFormalities = data.legalFormalitiesIndexPage.legalFormalities
  const total = data.legalFormalitiesIndexPage.pagination.total

  const formatDate = (date: string | null) =>
    date ? dayjs(date).format('DD/MM/YYYY') : null

  const dataSource = legalFormalities.map(
    (legalFormalityIndex: any): LegalFormalityIndexTableType => ({
      key: legalFormalityIndex.id,
      id: legalFormalityIndex.id,
      contactId: legalFormalityIndex.contactId,
      formProvider: legalFormalityIndex.formProvider,
      domiciliationId: legalFormalityIndex.domiciliationId,
      countryCode: legalFormalityIndex.countryCode,
      purchaseId: legalFormalityIndex.purchaseId,
      purchaseStatus: t(
        `purchases:status.${legalFormalityIndex.purchaseStatus}`,
        ''
      ),
      legalFormalityStatus: t(
        `legalFormalities:status.name.${legalFormalityIndex.legalFormalityStatus}`,
        ''
      ),
      legalStatus: legalFormalityIndex.legalStatus,
      purchaseType: legalFormalityIndex.purchaseType,
      productIdentificationToken: t(
        `products:identificationToken.${legalFormalityIndex.productIdentificationToken}`,
        ''
      ),
      societyName: legalFormalityIndex.societyName,
      firstname: legalFormalityIndex.firstname,
      lastname: legalFormalityIndex.lastname,
      email: legalFormalityIndex.email,
      createdAt: formatDate(legalFormalityIndex.createdAt),
      assignedUserEmail: legalFormalityIndex.assignedUserEmail,
      paidAt: formatDate(legalFormalityIndex.paidAt),
      formReceivedAt: formatDate(legalFormalityIndex.formReceivedAt),
      documentGeneratedAt: formatDate(legalFormalityIndex.documentGeneratedAt),
      waitingCapitalDepositAt: formatDate(
        legalFormalityIndex.waitingCapitalDepositAt
      ),
      signatureSentAt: formatDate(legalFormalityIndex.signatureSentAt),
      signatureSignedAt: formatDate(legalFormalityIndex.signatureSignedAt),
      legalAuthorityDroppedAt: formatDate(
        legalFormalityIndex.legalAuthorityDroppedAt
      ),
      legalAuthorityValidatedAt: formatDate(
        legalFormalityIndex.legalAuthorityValidatedAt
      ),
      internalNotes: legalFormalityIndex.internalNotes,
    })
  )

  return (
    <>
      <FormalitiesActionsWrapper>
        <SearchSection tableLoading={loading} />
        {ability.can('manage', 'LegalFormality') && (
          <FormalitiesActionsMenu tableLoading={loading} />
        )}
      </FormalitiesActionsWrapper>
      <Table
        dataSource={dataSource}
        columns={columns(t, variables, false, legalisteData)}
        pagination={{ ...variables.pagination, total }}
        scroll={{ x: 2200 }}
        onChange={handleTableChange}
        bordered
        size="small"
      />
    </>
  )
}

export default LegalFormalitiesTable

const FormalitiesActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px 0 8px 0;
`
